// Subscription state layer — Monthly Service Fee renewal (decouple dashboard vs cashback features).
//
// Kontrak target BE (epic): merchantStatus (PLUS|BASIC), platformFeeStatus, paidExpired, graceEndsAt.
// Kondisi BE saat ini (dicek dev 2026-07-22): profile GET /api/v1/partner/ baru punya
// plus (bool) + paidExpired (zero-date sentinel '0001-01-01T00:00:00Z') — belum ada
// merchantStatus/platformFeeStatus/graceEndsAt. Derivasi di bawah dipakai sampai BE expose
// field final; kalau field final muncul di response, dia yang menang.
//
// Demo 3 skenario tanpa BE (sama kayak msfMockBalance — aktif HANYA kalau key-nya di-set):
// sessionStorage.setItem('msfMockScenario', 'plus'|'grace'|'basic') lalu reload.
// Tanpa key itu, jalur produksi (derivasi/field BE) jalan normal.

// FALLBACK derivasi doang (env yang BE-nya belum expose field final). Panjang grace asli =
// config BE per env (API_APP_GRACE_PERIOD; dev = 5 hari) dan dibaca via graceEndsAt — angka
// ini JANGAN dianggap sumber kebenaran.
const GRACE_PERIOD_DAYS = 7;

const ZERO_DATE = '0001-01-01T00:00:00Z';
const STORAGE_KEY = 'skai1Subscription';
const RENEWAL_POPUP_SHOWN_KEY = 'skai1RenewalPopupShown';
export const SUBSCRIPTION_UPDATED_EVENT = 'subscription:updated';

export const MERCHANT_STATUS = {
  PLUS: 'PLUS',
  BASIC: 'BASIC',
};

export const SERVICE_FEE_STATUS = {
  ACTIVE: 'ACTIVE',
  GRACE: 'GRACE',
  INACTIVE: 'INACTIVE',
};

const DAY_MS = 24 * 60 * 60 * 1000;

function mockState(){
  const scenario = sessionStorage.getItem('msfMockScenario');
  const now = Date.now();

  if(scenario === 'plus'){
    return buildState(MERCHANT_STATUS.PLUS, SERVICE_FEE_STATUS.ACTIVE, new Date(now + 20 * DAY_MS), null);
  }
  if(scenario === 'basic'){
    return buildState(MERCHANT_STATUS.BASIC, SERVICE_FEE_STATUS.INACTIVE, new Date(now - 30 * DAY_MS), new Date(now - 23 * DAY_MS));
  }
  return buildState(MERCHANT_STATUS.PLUS, SERVICE_FEE_STATUS.GRACE, new Date(now - 3 * DAY_MS), new Date(now + 4 * DAY_MS));
}

function buildState(merchantStatus, serviceFeeStatus, paidExpired, graceEndsAt, serverTime, stripeLocation){
  return {
    merchantStatus,
    serviceFeeStatus,
    paidExpired: paidExpired ? new Date(paidExpired).toISOString() : null,
    graceEndsAt: graceEndsAt ? new Date(graceEndsAt).toISOString() : null,
    // Akun Stripe tempat subscription merchant SEBENARNYA berada (BE probe-resolved, bukan kolom DB —
    // BE_REPLY_MSF_MULTI_ACCOUNT_FINDINGS). Cuma ada buat non-ACTIVE (GRACE/INACTIVE); ACTIVE absen.
    // Dipakai RenewPayment buat render Stripe Elements di akun yg benar dari awal (hilangkan chicken-egg).
    stripeLocation: stripeLocation || null,
    // Selisih jam server vs client saat evaluasi — dipakai graceDaysRemaining biar
    // hitungan sisa hari ikut jam server (aturan epic), kebal jam device ngaco.
    clockSkewMs: serverTime ? new Date(serverTime).getTime() - Date.now() : 0,
    evaluatedAt: new Date().toISOString(),
  };
}

// Derive state dari data profile. Return null = data gak cukup buat nentuin status
// (belum pernah bayar apa pun) → caller pakai flow existing (funnel pricing), bukan dashboard.
// Catatan server-time: epic minta status dihitung dari server timestamp. Sampai BE kirim
// serverNow/status final, caller bisa oper serverNow (mis. dari header Date response);
// fallback ke jam client.
export function deriveSubscription(companyData, serverNow){
  if(sessionStorage.getItem('msfMockScenario')){
    return mockState();
  }

  if(!companyData){
    return null;
  }

  // Kontrak final BE (LIVE di dev 2026-07-23) menang atas derivasi.
  // Catatan kontrak: graceEndsAt ABSEN (bukan null) di luar GRACE; panjang grace = config
  // per env (dev kepasang 5 hari) → SELALU baca graceEndsAt, jangan hitung sendiri.
  if(companyData.merchantStatus){
    // Platform fee belum lunas → bukan urusan MSF: balikin null biar caller lempar
    // ke flow lama (funnel pricing/PartnerIncomplete), sesuai baris ke-4 matrix.
    if(companyData.platformFeeStatus === 'INACTIVE'){
      return null;
    }
    const merchantStatus = companyData.merchantStatus === MERCHANT_STATUS.PLUS ? MERCHANT_STATUS.PLUS : MERCHANT_STATUS.BASIC;
    const serviceFeeStatus = companyData.serviceFeeStatus || (merchantStatus === MERCHANT_STATUS.PLUS ? SERVICE_FEE_STATUS.ACTIVE : SERVICE_FEE_STATUS.INACTIVE);
    return buildState(merchantStatus, serviceFeeStatus, companyData.paidExpired || null, companyData.graceEndsAt || null, companyData.serverTime || null, companyData.stripeLocation || null);
  }

  const { plus, paidExpired } = companyData;
  const hasExpiry = paidExpired && paidExpired !== ZERO_DATE;
  const now = serverNow ? new Date(serverNow) : (companyData.serverTime ? new Date(companyData.serverTime) : new Date());

  if(!hasExpiry){
    // Gak ada jejak pembayaran monthly fee: plus legacy dianggap aktif; selain itu
    // gak bisa dibedain dari yang belum bayar platform fee → biar flow existing yang handle.
    return plus ? buildState(MERCHANT_STATUS.PLUS, SERVICE_FEE_STATUS.ACTIVE, null, null) : null;
  }

  const expiredDate = new Date(paidExpired);
  const graceEndsAt = new Date(expiredDate.getTime() + GRACE_PERIOD_DAYS * DAY_MS);

  // Aturan epic: now == paidExpired dihitung Grace Period (belum Expired).
  if(now < expiredDate){
    return buildState(MERCHANT_STATUS.PLUS, SERVICE_FEE_STATUS.ACTIVE, expiredDate, null);
  }
  // Derivasi = env TANPA field BE → simpan graceEndsAt null biar graceEndsAtDisplay fallback ke
  // `paidExpired + grace` (§5), BUKAN pakai turunan +7 basi. Status tetap dari boundary lokal di atas.
  if(now <= graceEndsAt){
    return buildState(MERCHANT_STATUS.PLUS, SERVICE_FEE_STATUS.GRACE, expiredDate, null);
  }
  return buildState(MERCHANT_STATUS.BASIC, SERVICE_FEE_STATUS.INACTIVE, expiredDate, null);
}

// Simpan + broadcast. Status gak boleh di-cache lintas session (aturan epic) → sessionStorage.
export function saveSubscription(state){
  if(state){
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }else{
    sessionStorage.removeItem(STORAGE_KEY);
  }
  window.dispatchEvent(new CustomEvent(SUBSCRIPTION_UPDATED_EVENT, { detail: state }));
}

export function loadSubscription(){
  try{
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  }catch(error){
    return null;
  }
}

export function clearSubscription(){
  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(RENEWAL_POPUP_SHOWN_KEY);
  sessionStorage.removeItem('skai1FeeBannerDismissed');
  sessionStorage.removeItem('renewalFlow');
}

// "Grace Period Ends" + "Remaining Days" di popup — KONTRAK v2 BE
// (docs/working/FE_MSF_GRACE_DISPLAY_CONTRACT.md, update 2026-08-04). Dua aturan:
//   1) JANGAN hitung dari `paidExpired` / meniru formula BE (logika "tanggal 4 +1" dsb) — §1b:
//      itu "resep desync permanen" (dev vs prod beda 1 hari buat row non-standar sampai formula naik).
//   2) Tanggal display = `graceEndsAt` (field BE, UDAH dinormalkan server) − 1 hari. Keputusan lead:
//      tanggal display = deadline bayar; 1 hari terakhir = buffer tersembunyi (akses gak mati sebelum
//      tanggal yg tampil, ada 1 hari cadangan sesudahnya).
// Remaining = max(0, displayEnd − serverDate) HARI penuh (date-part UTC). Floor 0 — di hari buffer
//   tampil "0 days" (by design §3); JANGAN min 1, JANGAN negatif.
// Semua operasi tanggal di UTC & date-part doang — jam 23:59:59 itu artefak normalisasi server,
//   jangan dirender (§4; PopServiceFee format pakai moment.utc).
//
// FALLBACK (§5) — CUMA kalau graceEndsAt ABSENT (env/response tanpa field baru, mis. prod sebelum
//   release MSF): displayEnd = datePart(paidExpired) + gracePeriod. Aproksimasi; row legacy bisa
//   meleset 1 hari (terima, populasinya 9 row). Begitu field selalu ada, hapus fallback ini.
const GRACE_PERIOD_DAYS_FALLBACK = 5; // = API_APP_GRACE_PERIOD BE. HANYA buat fallback §5.

// ms @ 00:00:00 UTC dari tanggal `value` — jam dibuang biar operasi −1/+N gak geser TZ (§4).
function dateOnlyUTCms(value){
  const d = new Date(value);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export function graceEndsAtDisplay(state){
  if(!state){
    return null;
  }
  // PRIMARY: graceEndsAt dari BE (authoritative, udah dinormalkan) → tampil − 1 hari.
  if(state.graceEndsAt){
    return new Date(dateOnlyUTCms(state.graceEndsAt) - DAY_MS).toISOString();
  }
  // FALLBACK §5: tanpa field BE → datePart(paidExpired) + gracePeriod.
  if(state.paidExpired){
    return new Date(dateOnlyUTCms(state.paidExpired) + GRACE_PERIOD_DAYS_FALLBACK * DAY_MS).toISOString();
  }
  return null;
}

export function graceDaysRemaining(state){
  const display = graceEndsAtDisplay(state);
  if(!display){
    return 0;
  }
  // Jam server (via clockSkewMs) → date-part UTC; diff HARI penuh, floor 0 (§3).
  const serverDateMs = dateOnlyUTCms(Date.now() + (state.clockSkewMs || 0));
  const diffDays = Math.round((new Date(display).getTime() - serverDateMs) / DAY_MS);
  return Math.max(0, diffDays);
}

export function isFeatureRestricted(state){
  return !!state && state.serviceFeeStatus === SERVICE_FEE_STATUS.INACTIVE;
}

// Popup MSF muncul sekali per login buat GRACE & BASIC/INACTIVE (dua-duanya dismissible).
// Buat BASIC, gating fitur PLUS + redirect Boost Activity ditangani terpisah (guardServiceFee /
// route guard Admin.js), bukan lewat popup non-dismissible.
export function shouldShowRenewalPopup(state){
  if(!state){
    return false;
  }
  if(state.serviceFeeStatus === SERVICE_FEE_STATUS.INACTIVE
     || state.serviceFeeStatus === SERVICE_FEE_STATUS.GRACE){
    return sessionStorage.getItem(RENEWAL_POPUP_SHOWN_KEY) !== 'true';
  }
  return false;
}

export function markRenewalPopupShown(){
  sessionStorage.setItem(RENEWAL_POPUP_SHOWN_KEY, 'true');
}

// Semua entry point renewal (popup/banner/feature gate) lewat sini.
// Renewal flow = halaman /product/renew (PurchaseLayout, ungated — merchant udah signed-in).
// Keputusan product 2026-07-24:
//   GRACE  → subscription masih ada (cuma settle invoice gagal) → SKIP pilih paket, langsung isi kartu.
//   EXPIRED/BASIC → subscription udah canceled (re-subscribe) → pilih paket dulu (plan page).
export function startRenewalFlow(navigate){
  sessionStorage.setItem('renewalFlow', 'true');
  const sub = loadSubscription();
  if(sub && sub.serviceFeeStatus === SERVICE_FEE_STATUS.GRACE){
    navigate('/product/renew/payment');
  }else{
    navigate('/product/renew');
  }
}

export function isRenewalFlow(){
  return sessionStorage.getItem('renewalFlow') === 'true';
}

export function endRenewalFlow(){
  sessionStorage.removeItem('renewalFlow');
}
